import React from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Divider, Link,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  CheckCircle as DotIcon,
  OpenInNew as ExternalIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

/**
 * Extension v5 — Popup (happy path)
 *
 * End-user surface. Clicking the toolbar icon opens a tiny Chrome
 * extension popup. True dimensions are 300x200 (maxed out with light
 * breathing room; Chrome popups max at ~800x600 but anything past
 * ~320x240 feels like "app inside toolbar" and reads as intrusive
 * for a silent runner).
 *
 * Contents:
 *  - Automatrix wordmark + v5.0.0 version
 *  - Connected dot + "scripts current as of 3m ago"
 *  - Optional recent activity ("Auto-signed into Khan Academy 2m ago")
 *  - A single "View details" link that opens the Automatrix dashboard
 *
 * NO script list, NO record button, NO agent UI, NO filters management.
 * All authoring lives in the Automatrix web dashboard.
 */

const TEAL = '#1ebea5';
const DEEP_TEAL = '#0b847f';
const POPUP_W = 300;
const POPUP_H = 200;

const MiniAchieveDxpIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16">
    <defs>
      <linearGradient id="popup-ok-bg" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor={TEAL} />
        <stop offset="100%" stopColor={DEEP_TEAL} />
      </linearGradient>
    </defs>
    <rect x="1" y="1" width="14" height="14" rx="3" fill="url(#popup-ok-bg)" />
    <path d="M 5 12 L 8 4 L 11 12 M 6.2 9.5 L 9.8 9.5"
          stroke="#fff" strokeWidth="1.2" fill="none"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PopupFrame = ({ children, h = POPUP_H }) => (
  <Paper elevation={6} sx={{
    width: POPUP_W, height: h,
    bgcolor: '#fff', borderRadius: 1.5, overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    border: '1px solid #d5d9df',
    fontFamily: '-apple-system, system-ui, sans-serif',
  }}>
    {children}
  </Paper>
);

const ExtV5PopupMockup = () => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 3, p: 4, bgcolor: '#eef1f5', minHeight: 'calc(100vh - 180px)',
  }}>
    <Box sx={{ textAlign: 'center', maxWidth: 640 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
        <ExtensionIcon sx={{ fontSize: 24, color: TEAL }} />
        <Typography variant="h6" fontWeight={700}>
          Popup — happy path
        </Typography>
        <Chip label="END USER" size="small" sx={{ fontWeight: 700, bgcolor: '#e8f5e9', color: '#1b5e20' }} />
        <Chip label="300 × 200" size="small" sx={{ fontWeight: 600, bgcolor: '#ede7f6', color: '#4a148c' }} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        What a facility user sees when they click the toolbar icon. The entire
        surface fits in Chrome's default extension popup dimensions. A single
        "View details" link deep-links to the Automatrix dashboard for anyone
        who needs more.
      </Typography>
    </Box>

    <Box sx={{
      display: 'flex', gap: 6, alignItems: 'flex-start',
      flexWrap: 'wrap', justifyContent: 'center',
    }}>
      {/* Main happy-path popup */}
      <Stack alignItems="center" spacing={1}>
        <Typography variant="caption" sx={{
          fontWeight: 700, fontSize: '0.72rem', color: '#546e7a',
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          Primary state · connected & idle
        </Typography>
        <PopupFrame>
          {/* Header */}
          <Box sx={{
            px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1,
            background: `linear-gradient(135deg, ${TEAL} 0%, ${DEEP_TEAL} 100%)`,
            color: '#fff',
          }}>
            <MiniAchieveDxpIcon size={20} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: 0.2 }}>
              AchieveDXP
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography sx={{
              fontSize: '0.68rem', fontFamily: 'monospace',
              bgcolor: 'rgba(255,255,255,0.18)', px: 0.75, py: 0.1,
              borderRadius: 0.5, fontWeight: 600,
            }}>
              v5.0.0
            </Typography>
          </Box>

          {/* Body — status */}
          <Box sx={{ px: 1.5, py: 1.25, flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
              <DotIcon sx={{ fontSize: 14, color: '#43a047' }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1b5e20' }}>
                Connected
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: '0.72rem', color: '#546e7a', ml: 2.25, mb: 1.25 }}>
              Scripts current as of 3m ago
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" alignItems="center" spacing={0.75}>
              <HistoryIcon sx={{ fontSize: 14, color: '#78909c' }} />
              <Typography sx={{ fontSize: '0.72rem', color: '#455a64' }}>
                Last sign-in: <b style={{ color: '#263238' }}>Khan Academy</b> · 2m ago
              </Typography>
            </Stack>
          </Box>

          {/* Footer link */}
          <Box sx={{
            px: 1.5, py: 0.75, borderTop: '1px solid #eceff2',
            bgcolor: '#faf7fd',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5,
          }}>
            <Link
              href="#" underline="none"
              sx={{
                fontSize: '0.7rem', color: TEAL, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 0.25,
              }}
            >
              View details
              <ExternalIcon sx={{ fontSize: 10 }} />
            </Link>
          </Box>
        </PopupFrame>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#90a4ae', maxWidth: 280, textAlign: 'center' }}>
          Version pill is plain, connected dot is green, "View details" opens the dashboard in a new tab.
        </Typography>
      </Stack>

      {/* Variant: nothing-recent popup */}
      <Stack alignItems="center" spacing={1}>
        <Typography variant="caption" sx={{
          fontWeight: 700, fontSize: '0.72rem', color: '#546e7a',
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          Variant · no recent activity
        </Typography>
        <PopupFrame h={170}>
          <Box sx={{
            px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1,
            background: `linear-gradient(135deg, ${TEAL} 0%, ${DEEP_TEAL} 100%)`,
            color: '#fff',
          }}>
            <MiniAchieveDxpIcon size={20} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: 0.2 }}>
              AchieveDXP
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography sx={{
              fontSize: '0.68rem', fontFamily: 'monospace',
              bgcolor: 'rgba(255,255,255,0.18)', px: 0.75, py: 0.1,
              borderRadius: 0.5, fontWeight: 600,
            }}>
              v5.0.0
            </Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1.25, flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
              <DotIcon sx={{ fontSize: 14, color: '#43a047' }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1b5e20' }}>
                Connected
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: '0.72rem', color: '#546e7a', ml: 2.25, mb: 0.5 }}>
              Scripts current as of 12m ago
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#90a4ae', ml: 2.25, fontStyle: 'italic' }}>
              No automations have run recently.
            </Typography>
          </Box>
          <Box sx={{
            px: 1.5, py: 0.75, borderTop: '1px solid #eceff2',
            bgcolor: '#faf7fd',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5,
          }}>
            <Link
              href="#" underline="none"
              sx={{
                fontSize: '0.7rem', color: TEAL, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 0.25,
              }}
            >
              View details
              <ExternalIcon sx={{ fontSize: 10 }} />
            </Link>
          </Box>
        </PopupFrame>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#90a4ae', maxWidth: 280, textAlign: 'center' }}>
          If nothing has run yet, we skip the "last sign-in" line entirely rather than saying "never".
        </Typography>
      </Stack>

      {/* Variant: update available */}
      <Stack alignItems="center" spacing={1}>
        <Typography variant="caption" sx={{
          fontWeight: 700, fontSize: '0.72rem', color: '#546e7a',
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          Variant · update available
        </Typography>
        <PopupFrame h={200}>
          <Box sx={{
            px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1,
            background: `linear-gradient(135deg, ${TEAL} 0%, ${DEEP_TEAL} 100%)`,
            color: '#fff',
          }}>
            <MiniAchieveDxpIcon size={20} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: 0.2 }}>
              AchieveDXP
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography sx={{
              fontSize: '0.68rem', fontFamily: 'monospace',
              bgcolor: 'rgba(255,255,255,0.18)', px: 0.75, py: 0.1,
              borderRadius: 0.5, fontWeight: 600,
            }}>
              v5.0.0
            </Typography>
          </Box>
          <Box sx={{ px: 1.5, py: 1.25, flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
              <DotIcon sx={{ fontSize: 14, color: '#43a047' }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1b5e20' }}>
                Connected
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: '0.72rem', color: '#546e7a', ml: 2.25, mb: 1 }}>
              Scripts current as of 3m ago
            </Typography>
            <Box sx={{
              bgcolor: '#fff3e0', border: '1px solid #ffcc80',
              borderRadius: 1, px: 1, py: 0.75, display: 'flex', gap: 0.5,
              alignItems: 'center',
            }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#e65100', fontWeight: 600 }}>
                Update available
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#bf360c', fontFamily: 'monospace' }}>
                v5.0.1
              </Typography>
            </Box>
          </Box>
          <Box sx={{
            px: 1.5, py: 0.75, borderTop: '1px solid #eceff2',
            bgcolor: '#faf7fd',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5,
          }}>
            <Link
              href="#" underline="none"
              sx={{
                fontSize: '0.7rem', color: TEAL, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 0.25,
              }}
            >
              View details
              <ExternalIcon sx={{ fontSize: 10 }} />
            </Link>
          </Box>
        </PopupFrame>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#90a4ae', maxWidth: 280, textAlign: 'center' }}>
          Non-blocking banner. User can keep working; update prompt routes to dashboard install flow.
        </Typography>
      </Stack>
    </Box>

    <Paper elevation={0} sx={{
      maxWidth: 700, p: 2, bgcolor: '#fff3cd',
      border: '1px solid #ffe082', borderRadius: 1.5,
    }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#5d4037', fontSize: '0.75rem' }}>
        WHAT'S INTENTIONALLY MISSING
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#4e342e', mt: 0.5 }}>
        No script list, no record button, no agent UI, no filter rule count,
        no step-by-step controls. Those belong in the Automatrix web dashboard
        (staff-facing) OR in the separate <code>/mockups/ext-admin</code> surface
        for engineers debugging in real browser tabs.
      </Typography>
    </Paper>
  </Box>
);

export default ExtV5PopupMockup;
